/**
 * Model Loader Utility
 * Loads GLB/GLTF models into Three.js scenes
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

// Singleton loader instances
let gltfLoader = null;
let dracoLoader = null;

/**
 * Initialize the GLTF loader with DRACO compression support
 */
function getLoader() {
  if (!gltfLoader) {
    gltfLoader = new GLTFLoader();

    // Set up DRACO decoder for compressed models
    dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    gltfLoader.setDRACOLoader(dracoLoader);
  }
  return gltfLoader;
}

/**
 * Load a GLB/GLTF model
 * @param {string} url - URL to the model file
 * @param {object} options - Loading options
 * @returns {Promise<THREE.Group>} - The loaded model
 */
export async function loadModel(url, options = {}) {
  const loader = getLoader();

  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        const model = gltf.scene;

        // Apply default transforms
        if (options.scale) {
          if (typeof options.scale === 'number') {
            model.scale.setScalar(options.scale);
          } else {
            model.scale.set(options.scale.x, options.scale.y, options.scale.z);
          }
        }

        if (options.position) {
          model.position.set(options.position.x, options.position.y, options.position.z);
        }

        if (options.rotation) {
          model.rotation.set(options.rotation.x, options.rotation.y, options.rotation.z);
        }

        // Enable shadows
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = options.castShadow ?? true;
            child.receiveShadow = options.receiveShadow ?? true;
          }
        });

        // Store animations if present
        if (gltf.animations && gltf.animations.length > 0) {
          model.userData.animations = gltf.animations;
          model.userData.mixer = new THREE.AnimationMixer(model);
        }

        resolve(model);
      },
      (progress) => {
        if (options.onProgress) {
          const percent = (progress.loaded / progress.total) * 100;
          options.onProgress(percent);
        }
      },
      (error) => {
        console.error('Error loading model:', error);
        reject(error);
      }
    );
  });
}

/**
 * Play an animation on a loaded model
 * @param {THREE.Group} model - The model with animations
 * @param {string|number} animationName - Name or index of the animation
 * @param {object} options - Animation options
 */
export function playAnimation(model, animationName = 0, options = {}) {
  if (!model.userData.mixer || !model.userData.animations) {
    console.warn('Model has no animations');
    return null;
  }

  const animations = model.userData.animations;
  const clip = typeof animationName === 'number'
    ? animations[animationName]
    : animations.find(a => a.name === animationName);

  if (!clip) {
    console.warn(`Animation "${animationName}" not found`);
    return null;
  }

  const action = model.userData.mixer.clipAction(clip);
  action.loop = options.loop ?? THREE.LoopRepeat;
  action.clampWhenFinished = options.clampWhenFinished ?? false;
  action.play();

  return action;
}

/**
 * Update all model animations (call in animation loop)
 * @param {THREE.Group[]} models - Array of models with animations
 * @param {number} deltaTime - Time since last frame
 */
export function updateAnimations(models, deltaTime) {
  models.forEach(model => {
    if (model.userData.mixer) {
      model.userData.mixer.update(deltaTime);
    }
  });
}

/**
 * Cache for loaded models
 */
const modelCache = new Map();

/**
 * Load a model with caching
 * @param {string} url - URL to the model
 * @param {object} options - Loading options
 * @returns {Promise<THREE.Group>} - Cloned model instance
 */
export async function loadCachedModel(url, options = {}) {
  if (!modelCache.has(url)) {
    const model = await loadModel(url, {});
    modelCache.set(url, model);
  }

  // Clone the cached model
  const cachedModel = modelCache.get(url);
  const clone = cachedModel.clone();

  // Apply instance-specific transforms
  if (options.scale) {
    if (typeof options.scale === 'number') {
      clone.scale.setScalar(options.scale);
    } else {
      clone.scale.set(options.scale.x, options.scale.y, options.scale.z);
    }
  }

  if (options.position) {
    clone.position.set(options.position.x, options.position.y, options.position.z);
  }

  if (options.rotation) {
    clone.rotation.set(options.rotation.x, options.rotation.y, options.rotation.z);
  }

  return clone;
}
